import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Modal } from './ui/Modal';
import PremiumFeatures from './Settings/PremiumFeatures';
import PremiumSubscribeButtons from './Settings/PremiumSubscribeButtons';
import { useCurrentUser } from '../services/userHooks';
import { getPremiumPricing } from '@/utils/premiumPricing';
import { submitStripeCheckout } from '@/utils/stripeCheckout';

interface InstapaperPremiumModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Modal heading; defaults to "Upgrade to Premium". */
  title?: string;
  /** Contextual lead-in explaining why the user hit the upsell (e.g. which
   *  Premium feature they tapped). Defaults to generic upgrade copy. */
  message?: string;
}

/**
 * Premium upsell modal for non-subscribers. Mirrors the legacy
 * `_modal_subscriber.html`: a contextual lead-in, the Premium feature grid, and
 * live Stripe checkout buttons. Callers pass `message`/`title` to tailor the
 * lead-in to the feature the user tried to use.
 */
function InstapaperPremiumModal({
  isOpen,
  onClose,
  title,
  message,
}: InstapaperPremiumModalProps) {
  const { currentUser } = useCurrentUser();
  const heading = title ?? t`Upgrade to Premium`;
  const leadIn = message ?? t`Upgrade today to access Instapaper Premium features.`;
  const pricing = getPremiumPricing(currentUser?.validStudentDiscount);
  const formKey = currentUser?.formKey;

  const handleSubscribe = (plan: 'monthly' | 'yearly') => {
    if (!formKey) return;
    submitStripeCheckout({ plan, formKey });
  };

  return (
    <Modal.Root
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      className="p-6"
      ariaLabel={heading}
    >
      <Modal.Header title={heading} />
      <Modal.Body className="space-y-2">
        <p className="text-copy-secondary">{leadIn}</p>
        <PremiumFeatures layout="rows" />
        {formKey && (
          <PremiumSubscribeButtons pricing={pricing} onSubscribe={handleSubscribe} />
        )}
        <p className="text-copy-muted pt-2 text-center text-xs">
          <Trans>
            See a{' '}
            <a href="/premium" className="text-link hover:underline">
              full list of Premium features
            </a>
            .
          </Trans>
        </p>
      </Modal.Body>
    </Modal.Root>
  );
}

export default InstapaperPremiumModal;
